/**
 * @fileoverview Hook to get preview user when not authenticated
 *
 * This hook provides access to the preview user for demo/preview mode.
 * It only loads the preview user when the user is NOT signed in.
 *
 * @module hooks/usePreviewUser
 */

import { useQuery } from 'convex/react'
import { useAuth } from '@clerk/clerk-react'
import { api } from 'convex/_generated/api'

/**
 * Get the preview user (only when not signed in)
 *
 * Returns the preview user object if not authenticated, undefined while loading,
 * or null if authenticated (should use regular user instead).
 *
 * @returns Preview user object, undefined if loading, or null if authenticated
 *
 * @example
 * ```tsx
 * const previewUser = usePreviewUser()
 * if (previewUser === undefined) return <div>Loading...</div>
 * if (previewUser === null) {
 *   // User is signed in, use regular user instead
 *   const user = useCurrentUser()
 * }
 * ```
 */
export function usePreviewUser() {
  const { isSignedIn } = useAuth()
  
  // Only query preview user if not signed in
  const previewUser = useQuery(
    api.preview.getPreviewUser,
    isSignedIn ? 'skip' : {}
  )

  // Return null if signed in (to indicate we shouldn't use preview user)
  if (isSignedIn) {
    return null
  }

  return previewUser
}

